import { provide } from "@lit/context";
import type { Permission } from "@pentacode/core";
import { css, html, LitElement } from "lit";
import { customElement, property } from "lit/decorators.js";
import "../../components/avatar/avatar";
import { enabledFeatures, type EnabledFeatures, loadEnabledFeatures } from "../../contexts/features-context";
import { app } from "../../init";
import { ErrorHandling } from "../../mixins/error-handling";
import { ready, type RouteArgs, Routing } from "../../mixins/routing";
import { ServiceWorker } from "../../mixins/service-worker";
import { StateMixin } from "../../mixins/state";
import "../../routes/join/join";
import "../../routes/select_company/select_company";
import { config, mixins, shared } from "../../styles";
import "../popover";
import "../set-password";
import "./home";
import "./login";
import "./settings";
// initial registration of all components (so you won't need to reregister in every file)
import "@pentacode/ui/components";
import { push } from "./push";

@customElement("ptc-staff-app")
export class StaffApp extends Routing(ServiceWorker(ErrorHandling(StateMixin(LitElement)))) {
    readonly routePattern = /^([^/]*)/;

    get helpPage(): string | undefined {
        return "handbuch/mitarbeiter-app";
    }

    @property({ type: Boolean, reflect: true, attribute: "singleton-container" })
    readonly singletonContainer = true;

    @property({ reflect: true })
    page: string = "";

    @provide({ context: enabledFeatures })
    private _features: EnabledFeatures = loadEnabledFeatures();

    private _pages = [
        "login",
        "home",
        "time",
        "tracking",
        "roster",
        "absences",
        "availabilities",
        "documents",
        "settings",
        "setpwd",
        "join",
        "select-company",
    ];

    constructor() {
        super();
        void app.loaded.then(() => {
            ready();
            if (app.state.company) {
                this._reloadFeatures();
            }
        });
    }

    private _reloadFeatures() {
        this._features = loadEnabledFeatures(app.company.isBetaCustomer);

        document.documentElement.dataset.features = Object.entries(this._features)
            .filter(([, value]) => value)
            .map(([feature]) => feature)
            .join(",");

        void push.init();
    }

    async handleRoute([page]: RouteArgs, _params: { [prop: string]: any }, path: string) {
        page = page as string;
        if (!app.account && !["login", "setpwd", "join"].includes(page)) {
            this.go("login", { next: path }, true);
            return;
        }

        if (app.account && !app.company && page !== "select-company") {
            this.go("select-company", { next: path });
            return;
        }

        if (
            !this._pages.includes(page) ||
            (["time", "tracking", "roster", "absences", "ledgers"].includes(page) &&
                !app.hasPermission(`staff.${page}` as Permission))
        ) {
            this.redirect("home");
            return;
        }

        const loadingScreen = document.querySelector(".loading-screen");
        loadingScreen?.setAttribute("aria-busy", "false");

        this.page = page;
    }

    static styles = [
        config,
        shared,
        css`
            :host {
                --font-family: "Nunito Variable";
                display: block;
                ${mixins.fullbleed()};
                display: flex;
                flex-direction: column;
                padding-top: env(safe-area-inset-top);
            }

            .main {
                flex: 1;
                align-self: center;
                width: 100%;
                box-sizing: border-box;
                max-width: 500px;
                display: flex;
                flex-direction: row;
                overflow: hidden;
                background: var(--color-bg);
                position: relative;
            }

            ptc-staff-home,
            ptc-staff-settings {
                transition:
                    transform 0.3s cubic-bezier(0.35, 0.35, 0.1, 1.1),
                    opacity 0.3s;
            }

            :host([page="settings"]) ptc-staff-home {
                transform: translate(-100%, 0);
                opacity: 0;
            }

            :host(:not([page="settings"])) ptc-staff-settings {
                transform: translate(100%, 0);
                opacity: 0;
            }

            @media (min-width: 500px) {
                :host {
                    padding: 1em;
                }

                .main {
                    border-radius: 1em;
                    margin: auto;
                    box-shadow: rgb(0 0 0 / 50%) 0px 0px 1em -0.5em;
                    max-height: 900px;
                }
            }
        `,
    ];

    render() {
        return html`
            <div class="main">
                <ptc-staff-home class="fullbleed"></ptc-staff-home>
                <ptc-staff-settings class="fullbleed"></ptc-staff-settings>
            </div>

            <ptc-staff-login
                @login-successful=${() => this._reloadFeatures()}
                class="fullbleed"
                ?hidden=${this.page !== "login"}
            ></ptc-staff-login>
            <ptc-select-company class="fullbleed" ?hidden=${this.page !== "select-company"}></ptc-select-company>
            <ptc-set-password class="fullbleed" ?hidden=${this.page !== "setpwd"}></ptc-set-password>
            <ptc-join-company class="fullbleed" ?hidden=${this.page !== "join"} .scope=${"staff"}></ptc-join-company>
        `;
    }
}
